import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
// import { ProductContext } from "@insite/client-framework/Components/ProductContext"; // Custom Code
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendImpressionTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendImpressionTracking";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
/* Custom Code
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@insite/content-library/Components/ProductCarouselProduct";
*/
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
// import { useEmblaCarousel } from "embla-carousel/react"; // Custom Code
import React, { createRef, useCallback, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

/* Custom Code - Start */
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import ga from "@store/Framework/Common/GoogleAnalytics";
import cssProp from "@store/Framework/Common/Utilities/cssProp";
import getMatchingDataFromDictionary from "@store/Framework/Common/Utilities/getMatchingDataFromDictionary";
import getWidgetComponent from "@store/Framework/Common/Utilities/getWidgetComponent";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import blockFullWidthField from "@store/Library/Components/Toolkit/Block/fields/fullWidth";
import blockMarginField from "@store/Library/Components/Toolkit/Block/fields/margin";
import blockVariantField from "@store/Library/Components/Toolkit/Block/fields/variant";
import blockVariantSideField from "@store/Library/Components/Toolkit/Block/fields/variantSide";
import Carousel, { carouselDefaultCustomCss, CarouselPresentationProps } from "@store/Overrides/Components/Carousel";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@store/Overrides/Components/ProductCarouselProduct";
import { productCardProps } from "@store/Themes/common/props/productCard";

export type ProductCarouselVariant = typeof ProductCarouselVariant[keyof typeof ProductCarouselVariant];
export const ProductCarouselVariant = {
    None: "none",
    ProductDetails: "productDetails",
} as const;

const AnchorHeading = getWidgetComponent("ProductDetails/ProductDetailsAnchorHeading");
/* Custom Code - End */

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    widgetPosition = "widgetPosition",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    visibleElementsTitle = "visibleElementsTitle",
    titleAlignment = "titleAlignment",
    customCSS = "customCSS",
    // Custom Code
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showDots = "showDots",
    variant = "variant",
    blockFullWidth = "blockFullWidth",
    blockVariant = "blockVariant",
    blockVariantSide = "blockVariantSide",
    blockMargin = "blockMargin",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.widgetPosition]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.visibleElementsTitle]: string;
        [fields.titleAlignment]: string;
        [fields.customCSS]: string;
        // Custom Code
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showDots]: boolean;
        [fields.variant]: ProductCarouselVariant;
        [fields.blockFullWidth]: boolean;
        [fields.blockVariant]: string;
        [fields.blockVariantSide]: string;
        [fields.blockMargin]: string;
    };
}

/* Custom Code - Start */
const titleAlignmentField = getTextAlignmentField(fields.titleAlignment);
/* Custom Code - End */

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps & HasProductContext /* Custom Code */) => {
    const carouselId = `${ownProps.id}-${ownProps.productContext?.product?.id}`; // Custom Code
    /* Store Custom Code - Start */
    const isDiscontinuedCustom = ownProps.productContext?.product?.properties?.isDiscontinuedCustom;
    /* Store Custom Code - End */

    return {
        products: getProductsForProductInfoList(state, carouselId), // Custom Code
        errorMessage: getErrorMessage(state, carouselId), // Custom Code
        pageType: getCurrentPage(state).type,
        productRecommendationWidgets: state.components.productRecommendations.widgets,
        session: state.context.session,
        // Custom Code
        productInfoByProductId:
            state.components.productInfoLists.productInfoListById[carouselId]?.productInfoByProductId,
        carouselId,
        // Store Custom Code
        isDiscontinuedCustom,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
    sendImpressionTracking,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    // Custom Code
    block?: BlockProps;
    carousel?: CarouselPresentationProps;
    prevArrowWrapper?: InjectableCss;
    nextArrowWrapper?: InjectableCss;
    dotsWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
}

export const productCarouselStyles: ProductCarouselStyles = {
    titleText: { variant: "h2" },
    carouselSlideInner: productCardProps.card,
    errorMessageText: { color: "danger" },
};

/* Custom Code - Start */
const defaultCustomCss = `.title {
}

${carouselDefaultCustomCss}`;

const rowTab = {
    displayName: "Row",
    sortOrder: 1,
};
/* Custom Code - End */

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2, // Custom Code
};

const styles = productCarouselStyles;

const ProductCarousel = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    productRecommendationWidgets,
    sendImpressionTracking,
    session,
    // Custom Code
    carouselId,
    productInfoByProductId,
    // Store Custom Code
    isDiscontinuedCustom,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);

    /* Custom Code - Start */
    const title =
        fields.title ||
        translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Carousel");

    const [canTrackViewItemList, setCanTrackViewItemList] = React.useState(false);
    const productInfos = getMatchingDataFromDictionary(products, productInfoByProductId);

    const trackViewItemList = ga.use.trackViewItemList(
        { items: products, item_list_name: title, item_list_id: fields.carouselType },
        { productInfos },
    );

    ga.use.productInfosCallback(
        productInfos,
        () => {
            setCanTrackViewItemList(false);
            trackViewItemList();
        },
        canTrackViewItemList,
    );
    /* Custom Code - End */

    useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId, // Custom Code
            carouselType: fields.carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: fields.displayProductsFrom,
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            widgetPosition: fields.widgetPosition,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
        setCanTrackViewItemList(true); // Custom Code
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        fields.widgetPosition,
        productContext?.product?.id,
        category?.id,
        brand?.id,
        productRecommendationWidgets,
        session?.pickUpWarehouse?.id,
    ]);

    // Removed - Embla implementation

    useEffect(() => {
        if (
            fields.carouselType !== "recommendedProducts" ||
            !productRecommendationWidgets ||
            fields.widgetPosition > productRecommendationWidgets.length ||
            !products
        ) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        const recommendationIds: number[] = [];
        products?.forEach(o => {
            const id = productRecommendationWidget.recs.find(r => r.refCode === o.detail?.name)?.id;
            if (id) {
                recommendationIds.push(id);
            }
        });

        if (recommendationIds.length) {
            sendImpressionTracking({ recommendationIds });
        }
    }, [fields.carouselType, productRecommendationWidgets, products]);

    const getProductRecommendationId = (product: ProductModel) => {
        if (!productRecommendationWidgets || fields.widgetPosition > productRecommendationWidgets.length) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        return productRecommendationWidget.recs.find(r => r.refCode === product.detail?.name)?.id;
    };

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    /* Store Custom Code - Start */
    if (fields.relatedProductType === "Replacement" && isDiscontinuedCustom?.toLowerCase() !== "true") {
        return null;
    }
    /* Store Custom Code - End */

    if (!products || products.length === 0) {
        return null;
    }

    // Moved - title - up
    const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    /* Custom Code - Start */
    const { blockFullWidth, blockVariant, blockVariantSide, blockMargin } = fields;
    const variant = fields.variant || ProductCarouselVariant.None;

    const titleAlignmentCss = css`
        ${cssProp("text-align", fields.titleAlignment)}
    `;

    const titleCss = css`
        ${titleAlignmentCss}
        ${styles.titleText?.css}
    `;

    return (
        <Block
            fullWidth={blockFullWidth}
            variant={blockVariant}
            variantSide={blockVariantSide}
            margin={blockMargin}
            containerCss={customCssWrapper.css}
            {...styles.block}
        >
            {title ? (
                variant === ProductCarouselVariant.ProductDetails ? (
                    <AnchorHeading
                        fields={{
                            title,
                            anchor: title,
                        }}
                        extendedStyles={{
                            anchorHeading: {
                                title: {
                                    css: titleAlignmentCss,
                                },
                            },
                        }}
                    />
                ) : (
                    <Typography {...styles.titleText} css={titleCss} className="title">
                        {title}
                    </Typography>
                )
            ) : null}
            <Carousel
                {...styles.carousel}
                data-test-selector={`productCarousel_${fields.carouselType}${fields.relatedProductType}`}
                id={`product-carousel-${id}`}
                maxNumberOfColumns={fields.maxNumberOfColumns}
                showDots={fields.showDots}
                slides={products.map(product => ({
                    id: product.id,
                    renderComponent: () => {
                        return (
                            <StyledWrapper
                                {...styles.carouselSlideInner}
                                id={`product_${product.id}`}
                                data-test-key={product.id}
                                data-test-selector="carousel_slide"
                            >
                                <ProductCarouselProduct
                                    carouselId={carouselId} // Custom Code
                                    product={product}
                                    recommendationId={getProductRecommendationId(product)}
                                    showImage={fields.showImage}
                                    showBrand={showBrandBlocks}
                                    showTitle={fields.showTitle}
                                    showPartNumbers={fields.showPartNumbers}
                                    showPrice={fields.showPrice}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList}
                                    showImageBadges={fields.showImageBadges}
                                    maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                    showTextBadges={fields.showTextBadges}
                                    maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                    extendedStyles={styles.carouselProductStyles}
                                    // Custom Code
                                    showAvailability={fields.showAvailability}
                                    showAttributes={fields.showAttributes}
                                />
                            </StyledWrapper>
                        );
                    },
                }))}
            />
        </Block>
    );
    /* Custom Code - End */
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
    { value: "recommendedProducts", displayName: "Recommended Products" },
];

export const ContentWrapper = styled.div<InjectableCss & { alignment: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    /* Custom Code - Start */
    component: withProductContext(
        connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductCarousel))),
    ),
    /* Custom Code - End */
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            // Removed - fields.titleAlignment
            /* Custom Code - Start */
            {
                ...titleAlignmentField.definition,
                displayName: "Title Alignment",
                defaultValue: textAlignmentOptions.center.id,
                fieldType: "Contextual",
            },
            {
                name: fields.variant,
                displayName: "Variant",
                editorTemplate: "DropDownField",
                options: [{ displayName: "Product Details", value: ProductCarouselVariant.ProductDetails }],
                defaultValue: ProductCarouselVariant.None,
                fieldType: "Contextual",
            },
            /* Custom Code - End */
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "crossSells",
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
                customFilter: (item, pageType, settings) => {
                    const {
                        settingsCollection: {
                            websiteSettings: { enableDynamicRecommendations },
                            productSettings: { enableProductRecommendations },
                        },
                    } = settings;

                    if (!enableDynamicRecommendations && item.value === "topSellers") {
                        return false;
                    }

                    if (!enableProductRecommendations && item.value === "recommendedProducts") {
                        return false;
                    }

                    if (
                        (pageType.type !== "ProductDetailsPage" && item.value === "relatedProducts") ||
                        (pageType.type !== "ProductDetailsPage" &&
                            pageType.type !== "CartPage" &&
                            item.value === "customersAlsoPurchased")
                    ) {
                        return false;
                    }

                    return true;
                },
            },
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                /* Removed - sortOrder */
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                /* Removed - sortOrder */
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: (item, _advancedFeaturesEnabled, _theme, pageType) =>
                    pageType === "ProductDetailsPage" && item.fields[fields.carouselType] === "customersAlsoPurchased",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 4,
                defaultValue: 4,
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.widgetPosition,
                displayName: "Widget Position",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 1,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "recommendedProducts",
                /* Removed - sortOrder */
            },
            // Removed - visibleElementsTitle
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                /* Removed - isEnabled */
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                /* Removed - isEnabled */
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            /* Custom Code - Start */
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            /* Custom Code - End */
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                /* Removed - sortOrder */
                tab: basicTab,
            },
            /* Custom Code - Start */
            {
                name: fields.showDots,
                displayName: "Show Dots",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                ...blockVariantField,
                name: fields.blockVariant,
                fieldType: "Contextual",
                tab: rowTab,
            },
            {
                ...blockVariantSideField,
                name: fields.blockVariantSide,
                fieldType: "Contextual",
                tab: rowTab,
            },
            {
                ...blockMarginField,
                name: fields.blockMargin,
                fieldType: "Contextual",
                tab: rowTab,
            },
            {
                ...blockFullWidthField,
                name: fields.blockFullWidth,
                fieldType: "Contextual",
                tab: rowTab,
            },
            /* Custom Code - End */
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) =>
                    item.fields[fields.variant] !== ProductCarouselVariant.ProductDetails &&
                    !!shouldDisplayAdvancedFeatures, // Custom Code
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
